/**
 * Statutory documents — `/v1/admin/documents/*` (doc 04-backend/30 §4.4): the invoice register,
 * the gap audit, the GSTR-1 export and the re-render. A filed document is never edited, so there
 * is no update hook here; a correction is a credit note, a new document.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import { idempotent } from '../idempotency';
import type { components } from '../../../api/generated/schema';

export type InvoiceRow = components['schemas']['InvoiceRow'];
export type InvoiceRegister = components['schemas']['InvoiceRegisterResponse'];
export type GapAudit = components['schemas']['GapAuditResponse'];

export interface DocumentQuery {
  fy: string;
  invoice_type?: string;
  page: number;
  page_size: number;
}

export function documentSearch(params: DocumentQuery): string {
  const q = new URLSearchParams({ fy: params.fy, page: String(params.page), page_size: String(params.page_size) });
  if (params.invoice_type) q.set('invoice_type', params.invoice_type);
  return q.toString();
}

export function useDocumentRegister(params: DocumentQuery, enabled: boolean) {
  return useQuery({
    queryKey: keys.documents.register({ ...params }),
    queryFn: () => authedClient.request<InvoiceRegister>(`/v1/admin/documents?${documentSearch(params)}`),
    enabled,
  });
}

export function useGapAudit(fy: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.documents.gapAudit(fy),
    queryFn: () => authedClient.request<GapAudit>(`/v1/admin/documents/gap-audit?fy=${fy}`),
    enabled,
  });
}

/** The GSTR-1 register for a year, as the CSV a CA files from. */
export function useDocumentExport() {
  return useMutation<string, unknown, { fy: string }>({
    mutationFn: ({ fy }) =>
      authedClient.request<string>(`/v1/admin/documents/register-export?fy=${fy}`, { parse: 'text' }),
  });
}

/** Draw a document's PDF again. The server refuses if the new PDF would say anything different. */
export function useReRender() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { code: string; reason: string }) =>
      authedClient.request<components['schemas']['ReRenderOut']>(`/v1/admin/documents/${args.code}/re-render`, {
        method: 'POST',
        body: { reason: args.reason },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.documents.all() }),
  });
}

export type CreditNoteRow = components['schemas']['CreditNoteRow'];

/**
 * A manual credit note — a classification fix, no money moving. Always four-eyes: the server
 * refuses without a seconder and refuses a seconder who is the requester. The key is minted when
 * the dialog opens, so a double-click files one note, not two.
 */
export function useCreateCreditNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      invoice_code: string;
      total_reversal_paise: number;
      reason: string;
      second_approver_admin_id: number;
      key: string;
    }) =>
      authedClient.request<CreditNoteRow>(
        `/v1/admin/documents/credit-notes?second_approver_admin_id=${args.second_approver_admin_id}`,
        {
          method: 'POST',
          headers: idempotent(args.key),
          body: {
            invoice_code: args.invoice_code,
            total_reversal_paise: args.total_reversal_paise,
            reason: args.reason,
          },
        },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.documents.all() }),
  });
}
